import React from 'react';

import { parseMarkdownLocationLink } from '@aline/common';
import ReactMarkdown from 'react-markdown';
import { Citation, counterResetStyles } from 'src/components/ai/shared/components/Citation';
import { openLink } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { usePlaybookNavigate } from './usePlaybookNavigate';

const useStyles = makeStyles()(() => ({
  container: {
    '& p': {
      margin: 0,
    },
    '& p + p': {
      marginTop: 8,
    },
    ...counterResetStyles,
  },
}));

/**
 * The anchor renderer for explanation markdown. Location links (`#doc/…`)
 * become numbered citation chips that scroll to and select the cited
 * paragraph — the same presentational chip chat citations use, minus the
 * chat-thread bookkeeping. Anything else is an ordinary link (a rule citing
 * an external policy URL, say) and opens outside the editor.
 */
function ExplanationAnchor({
  children,
  href,
}: {
  children?: React.ReactNode;
  href?: string;
}): JSX.Element | null {
  const { navigateToCardAnchor } = usePlaybookNavigate();
  if (!href) return null;

  const locationParts = href.startsWith('#') ? parseMarkdownLocationLink(href) : null;
  if (locationParts) {
    return (
      <Citation
        onClickCitation={event => {
          event.preventDefault();
          event.stopPropagation();
          navigateToCardAnchor({
            documentId: locationParts.documentId,
            locationAnchor: locationParts.locationId,
          });
        }}
      />
    );
  }
  // A `#` link that is not a valid location cites nothing — drop it.
  if (href.startsWith('#')) return null;

  return (
    <a
      href={href}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        openLink(href);
      }}
    >
      {children}
    </a>
  );
}

/**
 * Renders a position result's explanation: markdown whose citations were
 * validated server-side at run time (`convertCitationsWithParagraphState`),
 * so every location link points at a paragraph that existed when the run
 * read the document.
 */
export const PlaybookExplanationMarkdown = React.memo(function PlaybookExplanationMarkdown({
  markdown,
}: {
  markdown: string;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.container}>
      <ReactMarkdown components={{ a: ExplanationAnchor }}>{markdown}</ReactMarkdown>
    </div>
  );
});
